/**
 * serviceHealthRegistry — pure singleton observing service-call outcomes.
 *
 * Pushed to from the React Query mutation/query cache; pulled from by
 * useServiceHealthObservable (per-service status) and useConnectionObservable
 * (cross-service heartbeat-failure count + median latency). No React; the
 * hooks subscribe via useSyncExternalStore.
 *
 * Health rule (per service): the last 3 calls within a 60 s window must all
 * be failures to flip status to UNAVAILABLE. Any successful interleave keeps
 * the service HEALTHY. The rolling window per service caps at 10 calls.
 *
 * Spec: SN-IMPL-FE-003 Session 2D · plan §Architecture
 *       SN-FE-003 / Stage 4 / P2.D9 §3.2 (SERVICE_UNAVAILABLE state)
 */

export interface ServiceCall {
  ok: boolean;
  timestamp: number;
  latencyMs?: number;
}

export interface ServiceHealth {
  status: 'HEALTHY' | 'UNAVAILABLE';
  lastSuccessfulCallTimestamp: number | null;
}

const PER_SERVICE_WINDOW_CAP = 10;
const HEALTH_WINDOW_MS = 60_000;
const HEALTH_FAILURE_THRESHOLD = 3;

const calls: Map<string, ServiceCall[]> = new Map();
const subscribers: Map<string, Set<() => void>> = new Map();
const anySubscribers: Set<() => void> = new Set();

export function recordServiceCall(serviceId: string, call: ServiceCall): void {
  const existing = calls.get(serviceId) ?? [];
  const next = [...existing, call];
  if (next.length > PER_SERVICE_WINDOW_CAP) {
    next.splice(0, next.length - PER_SERVICE_WINDOW_CAP);
  }
  calls.set(serviceId, next);

  const listeners = subscribers.get(serviceId);
  if (listeners) {
    for (const listener of listeners) {
      listener();
    }
  }
  for (const listener of anySubscribers) {
    listener();
  }
}

export function subscribeServiceHealth(
  serviceId: string,
  callback: () => void,
): () => void {
  const set = subscribers.get(serviceId) ?? new Set();
  set.add(callback);
  subscribers.set(serviceId, set);
  return () => {
    const current = subscribers.get(serviceId);
    if (current) {
      current.delete(callback);
    }
  };
}

/**
 * Cross-service notifier consumed by useConnectionObservable so the hook
 * recomputes heartbeat-failure count + median latency on every registry
 * update without polling.
 */
export function subscribeAny(callback: () => void): () => void {
  anySubscribers.add(callback);
  return () => {
    anySubscribers.delete(callback);
  };
}

export function getServiceHealth(serviceId: string): ServiceHealth {
  const window = calls.get(serviceId) ?? [];

  let lastSuccessfulCallTimestamp: number | null = null;
  for (const call of window) {
    if (call.ok) {
      lastSuccessfulCallTimestamp = call.timestamp;
    }
  }

  // Examine the last 3 calls. If all are failures AND span ≤ HEALTH_WINDOW_MS,
  // status flips to UNAVAILABLE. Successful interleave keeps HEALTHY.
  const lastThree = window.slice(-HEALTH_FAILURE_THRESHOLD);
  if (lastThree.length === HEALTH_FAILURE_THRESHOLD) {
    const allFailed = lastThree.every((c) => !c.ok);
    if (allFailed) {
      const span = lastThree[lastThree.length - 1].timestamp - lastThree[0].timestamp;
      if (span <= HEALTH_WINDOW_MS) {
        return { status: 'UNAVAILABLE', lastSuccessfulCallTimestamp };
      }
    }
  }

  return { status: 'HEALTHY', lastSuccessfulCallTimestamp };
}

export function getRecentFailureCount(nowMs: number, windowMs: number): number {
  const lowerBound = nowMs - windowMs;
  let count = 0;
  for (const window of calls.values()) {
    for (const call of window) {
      if (!call.ok && call.timestamp >= lowerBound && call.timestamp <= nowMs) {
        count += 1;
      }
    }
  }
  return count;
}

export function getRecentLatencies(limit: number): number[] {
  const successful: ServiceCall[] = [];
  for (const window of calls.values()) {
    for (const call of window) {
      if (call.ok && typeof call.latencyMs === 'number') {
        successful.push(call);
      }
    }
  }
  successful.sort((a, b) => b.timestamp - a.timestamp);
  return successful.slice(0, limit).map((c) => c.latencyMs as number);
}

/** Test-only reset; not exported from the public hooks barrel. */
export function __resetForTest(): void {
  calls.clear();
  subscribers.clear();
  anySubscribers.clear();
}
